import React, { useEffect, useState, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, CircleMarker, Tooltip } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { motion } from "framer-motion";
import { Wand2, Send, Sparkles, MapPin, Users, ListTodo, Activity, Mic } from "lucide-react";
import { PageHeader } from "@/components/ui/PageHeader";
import { GlassCard } from "@/components/ui/GlassCard";
import { GlassInput } from "@/components/ui/GlassInput";
import { NxButton } from "@/components/ui/NxButton";
import { NxBadge } from "@/components/ui/NxBadge";
import { StatCard } from "@/components/ui/StatCard";
import { Skeleton } from "@/components/ui/Skeleton";
import { NxAvatar } from "@/components/ui/NxAvatar";
import { apiGet, apiPost } from "@/lib/api";
import pt from "@/lib/i18n";

// Fix default markers — use SVG
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
    iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
    shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const LUANDA = [-8.8368, 13.2343];

const STATUS_COLOR = {
    disponivel: "#2D7A4F",
    em_servico: "#C87941",
    pausa: "#94A3B8",
    folga: "#94A3B8",
    indisponivel: "#C0392B",
};

const PRIORITY_COLOR = { baixa: "#94A3B8", media: "#2D6A8A", alta: "#C87941", critica: "#C0392B" };
const STATUS_LABEL = pt.fieldOps?.statuses || { por_atribuir: "Por Atribuir", atribuido: "—", em_deslocamento: "—", em_progresso: "Em Progresso", concluido: "—", nao_cumprido: "—" };

const COLS = ["por_atribuir", "atribuido", "em_progresso", "concluido"];

const EXAMPLES = pt.fieldOps?.nlqSamples || [];

const FieldOpsPage = () => {
    const [stats, setStats] = useState(null);
    const [techs, setTechs] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [draggingId, setDraggingId] = useState(null);
    const [pulsedJobIds, setPulsedJobIds] = useState({});
    const [query, setQuery] = useState("");
    const [nlqLoading, setNlqLoading] = useState(false);
    const [nlqResult, setNlqResult] = useState(null);
    const [aiBusy, setAiBusy] = useState(false);

    const load = async () => {
        try {
            const [s, t, j] = await Promise.all([
                apiGet("/jobs/stats"),
                apiGet("/technicians"),
                apiGet("/jobs?page_size=200"),
            ]);
            setStats(s); setTechs(t || []); setJobs(j || []);
        } catch (e) { /* ignore */ }
    };

    useEffect(() => { load(); const id = setInterval(load, 12000); return () => clearInterval(id); }, []);

    const grouped = COLS.reduce((acc, c) => { acc[c] = (jobs || []).filter((j) => j.status === c); return acc; }, {});

    const pulse = (jobId) => {
        setPulsedJobIds((p) => ({ ...p, [jobId]: Date.now() }));
        setTimeout(() => setPulsedJobIds((p) => { const n = { ...p }; delete n[jobId]; return n; }), 1200);
    };

    const assignOne = async (jobId) => {
        try {
            const r = await apiPost("/jobs/assign", { job_id: jobId });
            pulse(jobId);
            load();
            return r;
        } catch (e) { return null; }
    };

    const aiAssignAll = async () => {
        setAiBusy(true);
        const unassigned = (jobs || []).filter((j) => j.status === "por_atribuir");
        for (const j of unassigned) {
            await assignOne(j.id);
            await new Promise((r) => setTimeout(r, 300));
        }
        setAiBusy(false);
    };

    const onDropToCol = async (status, jobId) => {
        // optimistic
        setJobs((arr) => arr.map((j) => (j.id === jobId ? { ...j, status } : j)));
        try { const { apiPatch } = await import("@/lib/api"); await apiPatch(`/jobs/${jobId}`, { status }); } catch { load(); }
    };

    const runNLQ = async (text) => {
        const q = (text || query).trim();
        if (!q) return;
        setNlqLoading(true); setNlqResult(null);
        try {
            const r = await apiPost("/nlq/query", { query: q });
            setNlqResult(r);
        } catch (e) {
            setNlqResult({ answer_text: "Falha ao consultar.", data: [] });
        } finally { setNlqLoading(false); }
    };

    return (
        <div data-testid="fieldops-root">
            <PageHeader
                title={pt.nav.fieldOps}
                subtitle={pt.fieldOps?.subtitle || "Despacho inteligente, mapa em tempo real e NLQ em Português."}
                right={
                    <NxButton variant="copper" icon={<Wand2 size={16} />} onClick={aiAssignAll} loading={aiBusy} testId="ai-assign-all">
                        Atribuição IA
                    </NxButton>
                }
            />

            {/* KPIs */}
            <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {stats === null ? [0,1,2,3].map((i) => <GlassCard key={i} padding="p-6"><Skeleton height="40px" /></GlassCard>) : (
                    <>
                        <StatCard label={pt.fieldOps?.kpiTechsAvailable || "Técnicos Disponíveis"} value={stats.available_technicians} index={0} testId="kpi-techs-available" />
                        <StatCard label="Trabalhos Hoje" value={stats.jobs_today} trend={{direction:"up", value: 6.2}} index={1} testId="kpi-jobs-today" />
                        <StatCard label="SLA Cumprido" value={stats.sla_compliance_pct} format="percent" trend={{direction:"up", value: 1.4}} index={2} testId="kpi-sla" />
                        <StatCard label="Trabalhos em Aberto" value={stats.open_jobs} trend={{direction:"down", value: 4.1}} index={3} testId="kpi-open" />
                    </>
                )}
            </section>

            {/* Map + Dispatch board */}
            <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-4 mb-6">
                <GlassCard padding="p-0" testId="live-map" className="overflow-hidden">
                    <div style={{ height: 520 }} className="rounded-[20px] overflow-hidden">
                        <MapContainer center={LUANDA} zoom={11} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
                            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
                            {techs.map((t) => (
                                <CircleMarker
                                    key={t.id}
                                    center={[t.current_lat || -8.84, t.current_lng || 13.23]}
                                    radius={9}
                                    pathOptions={{ color: "#FAF7F2", weight: 2, fillColor: STATUS_COLOR[t.availability_status] || "#94A3B8", fillOpacity: 0.95 }}
                                >
                                    <Tooltip>{t.user?.full_name || t.employee_code} · {t.current_zone}</Tooltip>
                                </CircleMarker>
                            ))}
                            {jobs.filter((j) => j.lat && j.lng).map((j) => {
                                const pulsing = !!pulsedJobIds[j.id];
                                return (
                                    <CircleMarker
                                        key={j.id}
                                        center={[j.lat, j.lng]}
                                        radius={pulsing ? 18 : 6}
                                        pathOptions={{
                                            color: "#FAF7F2", weight: pulsing ? 0 : 1,
                                            fillColor: PRIORITY_COLOR[j.priority] || "#C87941",
                                            fillOpacity: pulsing ? 0.35 : 0.85,
                                        }}
                                    >
                                        <Popup>
                                            <div style={{ minWidth: 200 }}>
                                                <div style={{ fontFamily: "JetBrains Mono", fontSize: 11, color: "#A0612E" }}>{j.job_number}</div>
                                                <div style={{ fontWeight: 600, marginTop: 4 }}>{j.customer?.full_name || "Cliente"}</div>
                                                <div style={{ fontSize: 12, color: "#4A5568", marginTop: 2 }}>{j.address}</div>
                                                <div style={{ marginTop: 6, fontSize: 11 }}>{STATUS_LABEL[j.status]} · {j.priority}</div>
                                                {j.status === "por_atribuir" && (
                                                    <button onClick={() => assignOne(j.id)} style={{ marginTop: 8, padding: "4px 10px", background: "#C87941", color: "white", borderRadius: 6, border: 0, cursor: "pointer", fontSize: 11 }}>{pt.fieldOps?.aiAssign || "Atribuir IA"}</button>
                                                )}
                                            </div>
                                        </Popup>
                                    </CircleMarker>
                                );
                            })}
                        </MapContainer>
                    </div>
                </GlassCard>

                {/* Dispatch board */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="dispatch-board">
                    {COLS.map((c) => (
                        <div key={c}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={() => draggingId && onDropToCol(c, draggingId)}
                            className="rounded-2xl p-3" style={{ background: "var(--bg-surface)", backdropFilter: "blur(20px)", border: "1px solid var(--glass-border)" }}
                            data-testid={`col-${c}`}
                        >
                            <div className="flex items-center justify-between mb-2 px-1">
                                <span className="font-display text-sm text-[color:var(--slate-deep)]">{STATUS_LABEL[c]}</span>
                                <NxBadge variant="neutral">{grouped[c].length}</NxBadge>
                            </div>
                            <div className="space-y-1.5 max-h-[210px] overflow-y-auto">
                                {grouped[c].slice(0, 8).map((j) => (
                                    <div key={j.id}
                                        draggable
                                        onDragStart={() => setDraggingId(j.id)}
                                        onDragEnd={() => setDraggingId(null)}
                                        className="glass-card glass-card-hover p-2.5 cursor-grab text-xs"
                                        style={{ borderLeft: `3px solid ${PRIORITY_COLOR[j.priority]}` }}
                                        data-testid={`job-card-${j.id}`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <p className="font-mono text-[10px] text-[color:var(--copper-dark)]">{j.job_number}</p>
                                            <NxBadge variant={j.priority === "critica" ? "error" : j.priority === "alta" ? "warning" : "info"}>{j.priority}</NxBadge>
                                        </div>
                                        <p className="font-body text-xs text-[color:var(--slate-deep)] mt-1 truncate font-medium">{j.customer?.full_name || "—"}</p>
                                        <p className="font-mono text-[10px] text-[color:var(--slate-mid)] mt-0.5">{j.zone} · {j.scheduled_time_window || ""}</p>
                                    </div>
                                ))}
                                {grouped[c].length === 0 && <p className="text-[10px] text-center text-[color:var(--slate-light)] py-3">{pt.common.noJobs}</p>}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* NLQ */}
            <GlassCard padding="p-6" testId="nlq-panel">
                <div className="flex items-center gap-2 mb-2">
                    <Sparkles size={18} className="text-[color:var(--copper)]" />
                    <h3 className="font-display text-xl text-[color:var(--slate-deep)]">{pt.common.fieldOpsAsk}</h3>
                </div>
                <p className="font-body text-xs text-[color:var(--slate-mid)] mb-4">Claude Sonnet 4 traduz a sua pergunta em consultas estruturadas.</p>

                <form onSubmit={(e) => { e.preventDefault(); runNLQ(); }} className="flex items-center gap-2 mb-3">
                    <div className="flex-1 relative">
                        <GlassInput
                            placeholder={pt.fieldOps?.nlqPlaceholder || "Mostra-me os técnicos disponíveis em Talatona…"}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            prefix={<Mic size={15} className="text-[color:var(--copper)]" />}
                            testId="nlq-input"
                        />
                    </div>
                    <NxButton variant="copper" type="submit" icon={<Send size={14} />} loading={nlqLoading} testId="nlq-submit">Perguntar</NxButton>
                </form>

                <div className="flex flex-wrap gap-2 mb-4">
                    {EXAMPLES.map((q) => (
                        <button key={q} type="button" onClick={() => { setQuery(q); runNLQ(q); }} className="text-xs px-3 py-1.5 rounded-full bg-[color:var(--copper-subtle)] text-[color:var(--copper-dark)] hover:bg-[color:var(--copper-glow)] transition-colors" data-testid="nlq-example">
                            {q}
                        </button>
                    ))}
                </div>

                {nlqResult && (
                    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="rounded-xl p-4 bg-[color:var(--bg-surface)] border border-[color:var(--copper-glow)]" data-testid="nlq-result">
                        <p className="font-display text-base text-[color:var(--slate-deep)]">{nlqResult.answer_text}</p>
                        {nlqResult.data?.length > 0 && (
                            <ul className="mt-3 space-y-1.5">
                                {nlqResult.data.slice(0, 6).map((row, i) => (
                                    <li key={i} className="text-xs text-[color:var(--slate-mid)] font-mono pl-3 border-l-2 border-[color:var(--copper-glow)]">
                                        {row.full_name || row.job_number || row.ticket_number || row.employee_code || row.order_number || JSON.stringify(row).slice(0, 80)}
                                        {row.current_zone && <span className="text-[color:var(--copper-dark)]"> · {row.current_zone}</span>}
                                        {row.zone && <span className="text-[color:var(--copper-dark)]"> · {row.zone}</span>}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </motion.div>
                )}
            </GlassCard>
        </div>
    );
};

export default FieldOpsPage;
